import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { feedService } from '../services/apiServices';
import { FeedType, FeedBrand, FeedStock } from '../types';

const queryKeys = {
  dashboard: ['feed', 'dashboard'] as const,
  types: ['feed', 'types'] as const,
  brands: ['feed', 'brands'] as const,
  stock: ['feed', 'stock'] as const,
  stockAlerts: ['feed', 'stock', 'alerts'] as const,
  procurements: ['feed', 'procurements'] as const,
  assignments: ['feed', 'assignments'] as const,
  trends: ['feed', 'trends'] as const,
  costAnalysis: ['feed', 'cost-analysis'] as const,
};

// Dashboard
export const useFeedDashboard = () => {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: feedService.getDashboard,
  });
};

// Types and Brands
export const useFeedTypes = () => {
  return useQuery<FeedType[]>({
    queryKey: queryKeys.types,
    queryFn: feedService.getTypes,
  });
};

export const useFeedBrands = () => {
  return useQuery<FeedBrand[]>({
    queryKey: queryKeys.brands,
    queryFn: feedService.getBrands,
  });
};

// Stock
export const useFeedStock = () => {
  return useQuery<FeedStock[]>({
    queryKey: queryKeys.stock,
    queryFn: feedService.getStock,
  });
};

export const useFeedStockAlerts = () => {
  return useQuery({
    queryKey: queryKeys.stockAlerts,
    queryFn: feedService.getStockAlerts,
  });
};

export const useUpdateStockThreshold = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ stockId, threshold }: { stockId: number; threshold: number }) =>
      feedService.updateStockThreshold(stockId, threshold),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

export const useUpdateStockQuantity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stockId,
      quantityKg,
      reason,
    }: {
      stockId: number;
      quantityKg: number;
      reason?: string;
    }) => feedService.updateStockQuantity(stockId, quantityKg, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      queryClient.invalidateQueries({ queryKey: queryKeys.stockAlerts });
    },
  });
};

// Procurements
export const useFeedProcurements = (params?: any) => {
  return useQuery({
    queryKey: [...queryKeys.procurements, params],
    queryFn: () => feedService.getProcurements(params),
  });
};

export const useProcureFeed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: feedService.procureFeed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.procurements });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

export const useMarkDelivered = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      procurementId,
      data,
    }: {
      procurementId: number;
      data: { quantity_delivered_kg: number; delivery_date: string };
    }) => feedService.markDelivered(procurementId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.procurements });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

export const useCancelProcurement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: feedService.cancelProcurement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.procurements });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

// Assignments
export const useFeedAssignments = (params?: any) => {
  return useQuery({
    queryKey: [...queryKeys.assignments, params],
    queryFn: () => feedService.getAssignments(params),
  });
};

export const useAssignFeed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: feedService.assignFeed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

export const useUpdateFeedAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      feedService.updateFeedAssignment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

export const useDeleteFeedAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => feedService.deleteFeedAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

export const useDeactivateFeedAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => feedService.deactivateFeedAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

export const useRecordConsumption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: feedService.recordConsumption,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

// Analytics
export const useFeedTrends = () => {
  return useQuery({
    queryKey: queryKeys.trends,
    queryFn: feedService.getTrends,
  });
};

export const useFeedCostAnalysis = () => {
  return useQuery({
    queryKey: queryKeys.costAnalysis,
    queryFn: feedService.getCostAnalysis,
  });
};

export const useFeedConversionRatio = (params?: {
  date_from?: string;
  date_to?: string;
  period?: 'weekly' | 'monthly';
  procurement_id?: number;
}) => {
  return useQuery({
    queryKey: ['feed', 'fcr', params],
    queryFn: () => feedService.getFCR(params),
  });
};

// Raw Materials
export const useRawMaterials = () => {
  return useQuery({
    queryKey: ['feed', 'raw-materials'],
    queryFn: feedService.getRawMaterials,
  });
};

export const useRawMaterialAlerts = () => {
  return useQuery({
    queryKey: ['feed', 'raw-materials', 'alerts'],
    queryFn: feedService.getRawMaterialAlerts,
  });
};

export const useRawMaterialProcurements = () => {
  return useQuery({
    queryKey: ['feed', 'raw-materials', 'procurements'],
    queryFn: feedService.getRawMaterialProcurements,
  });
};

export const useCreateRawMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: feedService.createRawMaterial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useUpdateRawMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      feedService.updateRawMaterial(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useAdjustRawMaterialStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { new_quantity: number; reason?: string } }) =>
      feedService.adjustRawMaterialStock(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials', 'alerts'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useProcureRawMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: feedService.procureRawMaterial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials', 'procurements'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useBulkProcureRawMaterials = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: feedService.bulkProcureRawMaterials,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials', 'procurements'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useMarkRawMaterialDelivered = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, delivery_date }: { id: number; delivery_date?: string }) =>
      feedService.markRawMaterialDelivered(id, delivery_date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials', 'procurements'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useDeleteRawMaterialProcurement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => feedService.deleteRawMaterialProcurement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials', 'procurements'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useRawMaterialHistory = (id: number | null) =>
  useQuery({
    queryKey: ['feed', 'raw-materials', id, 'history'],
    queryFn: () => feedService.getRawMaterialHistory(id as number),
    enabled: !!id,
  });

export const useToggleRawMaterialStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => feedService.toggleRawMaterialStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

// Feed Production
export const useFeedProfitBaseline = (days = 90) => {
  return useQuery({
    queryKey: ['feed', 'profit-baseline', days],
    queryFn: () => feedService.getProfitBaseline(days),
    staleTime: 5 * 60 * 1000,
  });
};

export const useFeedProductions = ({
  feed_brand_id,
  feed_type_id,
  date_from,
  date_to,
  bird_procurement_id,
}: {
  feed_brand_id?: number;
  feed_type_id?: number;
  date_from?: string;
  date_to?: string;
  bird_procurement_id?: number;
}) => {
  const params: any = {};
  if (feed_brand_id) params.feed_brand_id = feed_brand_id;
  if (feed_type_id) params.feed_type_id = feed_type_id;
  if (date_from) params.date_from = date_from;
  if (date_to) params.date_to = date_to;
  if (bird_procurement_id) params.bird_procurement_id = bird_procurement_id;
  return useQuery({
    queryKey: [
      'feed',
      'productions',
      { feed_brand_id, feed_type_id, date_from, date_to, bird_procurement_id },
    ],
    queryFn: () => feedService.getFeedProductions(params),
  });
};

export const useProduceFeed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: feedService.produceFeed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'productions'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'stock'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useUpdateFeedProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      feedService.updateFeedProduction({ id, data }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'productions'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'productions', id] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'stock'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useDeleteFeedProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => feedService.deleteFeedProduction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'productions'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'stock'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useAutoAssignProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => feedService.autoAssignProduction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'assignments'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'stock'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'dashboard'] });
    },
  });
};

export const useFeedProductionDetail = (id: number) => {
  return useQuery({
    queryKey: ['feed', 'productions', id],
    queryFn: () => feedService.getFeedProductionDetail(id),
    enabled: !!id && id > 0,
  });
};

// Feed Production Templates
export const useFeedProductionTemplates = (params?: {
  active?: boolean;
  feed_type_id?: number;
  feed_brand_id?: number;
}) => {
  return useQuery({
    queryKey: ['feed', 'templates', params],
    queryFn: () => feedService.getTemplates(params),
  });
};

export const useCreateFeedTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: feedService.createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'templates'] });
    },
  });
};

export const useUpdateFeedTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => feedService.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'templates'] });
    },
  });
};

export const useDeleteFeedTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => feedService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'templates'] });
    },
  });
};

export const useApplyFeedTemplate = () => {
  return useMutation({
    mutationFn: ({ id, quantityKg }: { id: number; quantityKg?: number }) =>
      feedService.applyTemplate(id, quantityKg),
  });
};

export const useProcurementPlanner = (weeks = 4) => {
  return useQuery({
    queryKey: ['feed', 'procurement-planner', weeks],
    queryFn: () => feedService.getProcurementPlanner(weeks),
    staleTime: 5 * 60 * 1000,
  });
};

export const useFeedTransitionSchedule = (brand = 'layermax') => {
  return useQuery({
    queryKey: ['feed', 'transition-schedule', brand],
    queryFn: () => feedService.getTransitionSchedule(brand),
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateRawMaterialProcurement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      feedService.updateRawMaterialProcurement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials', 'procurements'] });
    },
  });
};

export const useSupplierPayments = (supplierId: number | undefined) =>
  useQuery({
    queryKey: ['feed', 'suppliers', supplierId, 'payments'],
    queryFn: () => feedService.getSupplierPayments(supplierId as number),
    enabled: !!supplierId,
  });

export const useAddSupplierPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, data }: { supplierId: number; data: any }) =>
      feedService.addSupplierPayment(supplierId, data),
    onSuccess: (_res, { supplierId }) => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'suppliers', supplierId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'suppliers'] });
    },
  });
};

export const useDeleteSupplierPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, paymentId }: { supplierId: number; paymentId: number }) =>
      feedService.deleteSupplierPayment(supplierId, paymentId),
    onSuccess: (_res, { supplierId }) => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'suppliers', supplierId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'suppliers'] });
    },
  });
};

export const useSupplierRawMaterialUsage = (supplierId: number | undefined) =>
  useQuery({
    queryKey: ['feed', 'suppliers', supplierId, 'raw-material-usage'],
    queryFn: () => feedService.getSupplierRawMaterialUsage(supplierId as number),
    enabled: !!supplierId,
  });

// Suppliers
export const useSuppliers = (params?: any) =>
  useQuery({
    queryKey: ['feed', 'suppliers', params],
    queryFn: () => feedService.getSuppliers(params),
  });

export const useCreateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: feedService.createSupplier,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'suppliers'] });
    },
  });
};

export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => feedService.updateSupplier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'suppliers'] });
    },
  });
};

export const useToggleSupplierStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => feedService.toggleSupplierStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'suppliers'] });
    },
  });
};

export const useDeleteSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => feedService.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'suppliers'] });
    },
  });
};

export const useSupplyCategories = () =>
  useQuery({
    queryKey: ['feed', 'supply-categories'],
    queryFn: () => feedService.getSupplyCategories(),
  });

export const useCreateSupplyCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) => feedService.createSupplyCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'supply-categories'] });
    },
  });
};

export const useUpdateSupplyCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name: string } }) =>
      feedService.updateSupplyCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'supply-categories'] });
    },
  });
};

export const useDeleteSupplyCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => feedService.deleteSupplyCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'supply-categories'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'suppliers'] });
    },
  });
};

// ── Feed Switch Campaigns ──────────────────────────────────────────────────────

export const useSwitchCampaigns = () =>
  useQuery({
    queryKey: ['feed', 'switch-campaigns'],
    queryFn: () => feedService.getSwitchCampaigns(),
  });

export const useSwitchCampaign = (id: number) =>
  useQuery({
    queryKey: ['feed', 'switch-campaigns', id],
    queryFn: () => feedService.getSwitchCampaign(id),
    enabled: !!id,
  });

export const useCreateSwitchCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => feedService.createSwitchCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'switch-campaigns'] });
    },
  });
};

export const useUpdateSwitchCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      feedService.updateSwitchCampaign(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'switch-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'switch-campaigns', id] });
    },
  });
};

export const useAddSwitchGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: number; data: any }) =>
      feedService.addSwitchGroup(campaignId, data),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'switch-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'switch-campaigns', campaignId] });
    },
  });
};

export const useUpdateSwitchGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: number; data: any }) =>
      feedService.updateSwitchGroup(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'switch-campaigns'] });
    },
  });
};

export const useLogSwitchDaily = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: number; data: any }) =>
      feedService.logSwitchDaily(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'switch-campaigns'] });
    },
  });
};

export const useSwitchGroupLogs = (groupId: number) =>
  useQuery({
    queryKey: ['feed', 'switch-group-logs', groupId],
    queryFn: () => feedService.getSwitchGroupLogs(groupId),
    enabled: !!groupId,
  });

export const useDeleteSwitchCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => feedService.deleteSwitchCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'switch-campaigns'] });
    },
  });
};
